import { useFormikContext } from "formik";
import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AddressValidationAddress } from "../../server/partnerCenter/models/addressValidation";
import { countryCrmCodeToIso } from "../../server/crm/api/utils/countryCrmCodeToIso";
import { partnerCenterActions } from "../../redux/actions/partnerCenterActions";
import { OnboardingRootState } from "../../redux/reducers/rootReducer";
import {
  getAddressValidationOriginalAddress,
  getAddressValidationStatus,
  getSuggestedAddresses,
} from "../../redux/selectors/partnerCenterSelectors";
import { AddressSuggestionModal } from "../AddressSuggestionModal";

// Countries where the form uses a States dropdown (IsStateRequired + States array in countryValidations).
// For these countries, we preserve the user-selected region when applying a suggested address because
// the API response format may not match the dropdown values (e.g. "California" vs "CA").
export const COUNTRIES_WITH_STATE_DROPDOWN = new Set([
  "AU",
  "BD",
  "BS",
  "BR",
  "BZ",
  "CA",
  "CN",
  "CR",
  "ES",
  "GT",
  "HN",
  "IN",
  "IT",
  "JM",
  "JP",
  "MX",
  "MY",
  "NI",
  "PA",
  "CH",
  "AE",
  "TR",
  "US",
  "VE",
]);

type AddressValidatorProps = Readonly<{
  countryFieldName: string;
  regionFieldName: string;
  cityFieldName: string;
  addressLine1FieldName: string;
  addressLine2FieldName: string;
  postalCodeFieldName: string;
}>;

type AddressFormValues = Record<string, string>;

export const AddressValidator = ({
  countryFieldName,
  regionFieldName,
  cityFieldName,
  addressLine1FieldName,
  addressLine2FieldName,
  postalCodeFieldName,
}: AddressValidatorProps) => {
  const { values, setFieldValue } = useFormikContext<AddressFormValues>();
  const dispatch = useDispatch();

  const validationStatus = useSelector((state: OnboardingRootState) =>
    getAddressValidationStatus(state)
  );
  const suggestedAddresses = useSelector((state: OnboardingRootState) =>
    getSuggestedAddresses(state)
  );
  const originalAddress = useSelector((state: OnboardingRootState) =>
    getAddressValidationOriginalAddress(state)
  );

  const country = values[countryFieldName];
  const region = values[regionFieldName];
  const city = values[cityFieldName];
  const addressLine1 = values[addressLine1FieldName];
  const addressLine2 = values[addressLine2FieldName];
  const postalCode = values[postalCodeFieldName];

  const currentKey = `${country}|${region}|${city}|${addressLine1}|${addressLine2}|${postalCode}`;

  // Reset addressValidated and Redux state when address fields change (but not after suggestion confirm)
  const prevKeyRef = useRef(currentKey);
  const skipResetRef = useRef(false);
  useEffect(() => {
    if (prevKeyRef.current !== currentKey) {
      prevKeyRef.current = currentKey;
      if (skipResetRef.current) {
        skipResetRef.current = false;
      } else {
        setFieldValue("addressValidated", false);
        dispatch(partnerCenterActions.resetAddressValidation());
      }
    }
  }, [currentKey, setFieldValue, dispatch]);

  // Sync addressValidated Formik field when Redux status changes to "valid"
  useEffect(() => {
    if (validationStatus === "valid") {
      setFieldValue("addressValidated", true);
    }
  }, [validationStatus, setFieldValue]);

  const handleSuggestionConfirm = useCallback(
    (address: AddressValidationAddress) => {
      skipResetRef.current = true;
      if (address.city) {
        setFieldValue(cityFieldName, address.city);
      }
      if (address.address_line1) {
        setFieldValue(addressLine1FieldName, address.address_line1);
      }
      setFieldValue(addressLine2FieldName, address.address_line2 ?? "");
      if (address.postal_code) {
        setFieldValue(postalCodeFieldName, address.postal_code);
      }
      // Only overwrite region for countries without a state dropdown.
      // Countries with a dropdown may have mismatched formats (e.g. "California" vs "CA").
      const isoCountry = countryCrmCodeToIso[country] ?? country;
      if (address.region && !COUNTRIES_WITH_STATE_DROPDOWN.has(isoCountry)) {
        setFieldValue(regionFieldName, address.region);
      }
      dispatch(partnerCenterActions.confirmAddressSuggestion(address));
    },
    [
      country,
      cityFieldName,
      addressLine1FieldName,
      addressLine2FieldName,
      postalCodeFieldName,
      regionFieldName,
      setFieldValue,
      dispatch,
    ]
  );

  const handleSuggestionReject = useCallback(() => {
    dispatch(partnerCenterActions.resetAddressValidation());
  }, [dispatch]);

  const isSuggestionModalOpen = validationStatus === "suggestions" && suggestedAddresses.length > 0;

  return (
    <AddressSuggestionModal
      isOpen={isSuggestionModalOpen}
      suggestedAddresses={suggestedAddresses}
      originalAddress={
        originalAddress ?? {
          country: countryCrmCodeToIso[country] ?? country,
          region,
          city,
          address_line1: addressLine1,
          address_line2: addressLine2,
          postal_code: postalCode,
        }
      }
      onConfirm={handleSuggestionConfirm}
      onReject={handleSuggestionReject}
    />
  );
};
